import { createAsyncThunk } from "@reduxjs/toolkit";

import { getRefundRedirectToken } from "@repo/ui/api/getRefundRedirectToken";
import { getWalletDetails } from "@repo/ui/api/getWalletDetails";
import { getWalletHistory } from "@repo/ui/api/getWalletHistory";
import { getWalletSummary } from "@repo/ui/api/getWalletSummary";
import { getWithdrawalEligibleVouchers } from "@repo/ui/api/getWithdrawalEligibleVouchers";
import { getTabSessionIdFromHeaders } from "@repo/ui/lib/getTabSessionIdFromHeaders";
import type {
  WalletDetailsData,
  WalletMovementRecord,
  WalletRefundRedirectTokenData,
  WalletRefundRedirectTokenRequest,
  WalletSummaryData,
} from "@repo/ui/types/wallet";

export const fetchWalletSummary = createAsyncThunk<
  WalletSummaryData,
  string,
  { rejectValue: string }
>("wallet/fetchSummary", async (primaryContactEmail, { rejectWithValue }) => {
  const tabSessionId = await getTabSessionIdFromHeaders().catch(() => null);
  try {
    const response = await getWalletSummary(primaryContactEmail, tabSessionId);
    if (!response.data) return rejectWithValue("Empty response from wallet summary");
    return response.data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Failed to fetch wallet summary",
    );
  }
});

export const fetchWalletDetails = createAsyncThunk<
  WalletDetailsData,
  string,
  { rejectValue: string }
>("wallet/fetchDetails", async (primaryContactEmail, { rejectWithValue }) => {
  const tabSessionId = await getTabSessionIdFromHeaders().catch(() => null);
  try {
    const response = await getWalletDetails(primaryContactEmail, tabSessionId);
    if (!response.data) return rejectWithValue("Empty response from wallet details");
    return response.data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Failed to fetch wallet details",
    );
  }
});

export const fetchWithdrawalEligibleVouchers = createAsyncThunk<
  WalletDetailsData,
  string,
  { rejectValue: string }
>("wallet/fetchWithdrawalEligibleVouchers", async (primaryContactEmail, { rejectWithValue }) => {
  const tabSessionId = await getTabSessionIdFromHeaders().catch(() => null);
  try {
    const response = await getWithdrawalEligibleVouchers(primaryContactEmail, tabSessionId);
    if (!response.data) return rejectWithValue("Empty response from withdrawal-eligible vouchers");
    return response.data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Failed to fetch withdrawal-eligible vouchers",
    );
  }
});

export const fetchRefundRedirectToken = createAsyncThunk<
  WalletRefundRedirectTokenData,
  WalletRefundRedirectTokenRequest,
  { rejectValue: string }
>("wallet/fetchRefundRedirectToken", async (payload, { rejectWithValue }) => {
  const tabSessionId = await getTabSessionIdFromHeaders().catch(() => null);
  try {
    const response = await getRefundRedirectToken(payload, tabSessionId);
    if (!response.data) return rejectWithValue("Empty response from refund redirect token");
    return response.data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Failed to fetch refund redirect token",
    );
  }
});

export const fetchWalletHistory = createAsyncThunk<
  WalletMovementRecord[],
  string,
  { rejectValue: string }
>("wallet/fetchHistory", async (customerNumber, { rejectWithValue }) => {
  const tabSessionId = await getTabSessionIdFromHeaders().catch(() => null);
  try {
    const response = await getWalletHistory(customerNumber, tabSessionId);
    const records = Array.isArray(response)
      ? (response as unknown as WalletMovementRecord[])
      : response.data;
    return records ?? [];
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Failed to fetch wallet history",
    );
  }
});
